import React, {
    useEffect,
    useState
} from "react";

import Swal from "sweetalert2";

import {
    FaEdit,
    FaTrash,
    FaPlus,
    FaTimes,
    FaFilePdf,
    FaDownload
} from "react-icons/fa";

import API_BASE_URL from "../config/api";

import QuillEditor from "../components/QuillEditor";


const FreeStudyMaterial = () => {

    // =========================================================
    // STATES
    // =========================================================

    const [materials, setMaterials] = useState([]);

    const [subjects, setSubjects] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [showForm, setShowForm] = useState(false);

    const [editId, setEditId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);

    const [lastPage, setLastPage] = useState(1);

    const [formData, setFormData] = useState({
        title: "",
        subject: "",
        content: "",
        image: null,
        pdf: null
    });

    const [existingImage, setExistingImage] = useState("");

    const [existingPdf, setExistingPdf] = useState("");


    // =========================================================
    // API URL
    // =========================================================

    const API_URL =
        API_BASE_URL.replace(/\/$/, "");


    // =========================================================
    // STORAGE BASE URL
    // =========================================================

    const STORAGE_BASE_URL =
        API_URL.replace(/\/api$/, "");


    // =========================================================
    // IMAGE STORAGE
    // =========================================================

    const IMAGE_STORAGE_URL =
        `${STORAGE_BASE_URL}/storage/study`;


    // =========================================================
    // PDF STORAGE
    // =========================================================

    const PDF_STORAGE_URL =
        `${STORAGE_BASE_URL}/storage/jobs`;


    // =========================================================
    // FETCH SUBJECTS
    // =========================================================

    const fetchSubjects = async () => {

        try {

            const response =
                await fetch(
                    `${API_URL}/admin/free-study-material/subjects`,
                    {
                        method: "GET",

                        headers: {
                            "Accept": "application/json"
                        },

                        credentials: "include"
                    }
                );


            const data =
                await response.json();


            console.log(
                "SUBJECTS RESPONSE:",
                data
            );


            if (
                response.ok &&
                data.success
            ) {

                setSubjects(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );

            } else {

                setSubjects([]);

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        data.message ||
                        "Unable to fetch subjects."
                });
            }

        } catch (error) {

            console.error(
                "FETCH SUBJECTS ERROR:",
                error
            );

            setSubjects([]);

            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while loading subjects."
            });
        }
    };


    // =========================================================
    // FETCH MATERIALS
    // =========================================================

    const fetchMaterials = async (
        page = 1
    ) => {

        try {

            setLoading(true);


            const response =
                await fetch(
                    `${API_URL}/admin/free-study-material?page=${page}`,
                    {
                        method: "GET",

                        headers: {
                            "Accept": "application/json"
                        },

                        credentials: "include"
                    }
                );


            const data =
                await response.json();


            console.log(
                "FREE STUDY MATERIAL RESPONSE:",
                data
            );


            if (
                response.ok &&
                data.success
            ) {

                setMaterials(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );


                setCurrentPage(
                    data.pagination?.current_page ||
                    page
                );


                setLastPage(
                    data.pagination?.last_page ||
                    1
                );

            } else {

                setMaterials([]);

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        data.message ||
                        "Unable to fetch study materials."
                });
            }

        } catch (error) {

            console.error(
                "FETCH STUDY MATERIAL ERROR:",
                error
            );


            setMaterials([]);


            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while loading study materials."
            });

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // USE EFFECT
    // =========================================================

    useEffect(() => {

        fetchMaterials(1);

        fetchSubjects();

    }, []);


    // =========================================================
    // INPUT CHANGE
    // =========================================================

    const handleChange = (e) => {

        const {
            name,
            value,
            files
        } = e.target;


        // =====================================================
        // IMAGE
        // =====================================================

        if (name === "image") {

            setFormData((prev) => ({
                ...prev,

                image:
                    files &&
                    files.length > 0
                        ? files[0]
                        : null
            }));

            return;
        }


        // =====================================================
        // PDF
        // =====================================================

        if (name === "pdf") {

            setFormData((prev) => ({
                ...prev,

                pdf:
                    files &&
                    files.length > 0
                        ? files[0]
                        : null
            }));

            return;
        }


        // =====================================================
        // TEXT / SELECT
        // =====================================================

        setFormData((prev) => ({
            ...prev,

            [name]: value
        }));
    };


    // =========================================================
    // QUILL CONTENT CHANGE
    // =========================================================

    const handleContentChange = (content) => {

        setFormData((prev) => ({
            ...prev,
            content: content || ""
        }));
    };


    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setFormData({
            title: "",
            subject: "",
            content: "",
            image: null,
            pdf: null
        });

        setExistingImage("");

        setExistingPdf("");

        setEditId(null);

        setShowForm(false);
    };


    // =========================================================
    // ADD
    // =========================================================

    const handleAdd = () => {

        setFormData({
            title: "",
            subject: "",
            content: "",
            image: null,
            pdf: null
        });

        setExistingImage("");

        setExistingPdf("");

        setEditId(null);

        setShowForm(true);
    };


    // =========================================================
    // EDIT
    // =========================================================

    const handleEdit = async (id) => {

        try {

            setSaving(true);


            const response =
                await fetch(
                    `${API_URL}/admin/free-study-material/${id}`,
                    {
                        method: "GET",

                        headers: {
                            "Accept": "application/json"
                        },

                        credentials: "include"
                    }
                );


            const data =
                await response.json();


            console.log(
                "EDIT RESPONSE:",
                data
            );


            if (
                !response.ok ||
                !data.success
            ) {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        data.message ||
                        "Unable to fetch study material."
                });

                return;
            }


            const material =
                data.data;


            // =================================================
            // FORM DATA
            // =================================================

            setFormData({

                title:
                    material.title ||
                    "",

                subject:
                    material.subject ||
                    "",

                content:
                    material.content ||
                    "",

                image: null,

                pdf: null
            });


            // =================================================
            // EXISTING IMAGE
            // =================================================

            setExistingImage(
                material.image ||
                ""
            );


            // =================================================
            // EXISTING PDF
            // =================================================

            setExistingPdf(
                material.download ||
                ""
            );


            // =================================================
            // EDIT ID
            // =================================================

            setEditId(
                material.study_id
            );


            // =================================================
            // SHOW FORM
            // =================================================

            setShowForm(true);

        } catch (error) {

            console.error(
                "EDIT ERROR:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while loading study material."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        // =====================================================
        // TITLE VALIDATION
        // =====================================================

        if (!formData.title.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Title Required",
                text:
                    "Please enter study material title."
            });

            return;
        }


        // =====================================================
        // SUBJECT VALIDATION
        // =====================================================

        if (!formData.subject.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Subject Required",
                text:
                    "Please select a subject."
            });

            return;
        }


        // =====================================================
        // PDF REQUIRED FOR ADD
        // =====================================================

        if (
            !editId &&
            !formData.pdf
        ) {

            Swal.fire({
                icon: "warning",
                title: "PDF Required",
                text:
                    "Please select a PDF file."
            });

            return;
        }


        // =====================================================
        // IMAGE VALIDATION
        // =====================================================

        if (formData.image) {

            const allowedImages = [
                "image/jpeg",
                "image/png",
                "image/webp"
            ];


            if (
                !allowedImages.includes(
                    formData.image.type
                )
            ) {

                Swal.fire({
                    icon: "warning",
                    title: "Invalid Image",
                    text:
                        "Only JPG, JPEG, PNG and WEBP images are allowed."
                });

                return;
            }


            if (
                formData.image.size >
                5 * 1024 * 1024
            ) {

                Swal.fire({
                    icon: "warning",
                    title: "Image Too Large",
                    text:
                        "Image size must be less than 5 MB."
                });

                return;
            }
        }


        // =====================================================
        // PDF VALIDATION
        // =====================================================

        if (formData.pdf) {

            if (
                formData.pdf.type !==
                "application/pdf"
            ) {

                Swal.fire({
                    icon: "warning",
                    title: "Invalid PDF",
                    text:
                        "Only PDF files are allowed."
                });

                return;
            }


            if (
                formData.pdf.size >
                20 * 1024 * 1024
            ) {

                Swal.fire({
                    icon: "warning",
                    title: "PDF Too Large",
                    text:
                        "PDF size must be less than 20 MB."
                });

                return;
            }
        }


        // =====================================================
        // SAVE
        // =====================================================

        try {

            setSaving(true);


            const data =
                new FormData();


            // =================================================
            // TITLE
            // =================================================

            data.append(
                "title",
                formData.title.trim()
            );


            // =================================================
            // SUBJECT
            // =================================================

            data.append(
                "subject",
                formData.subject.trim()
            );


            // =================================================
            // CONTENT
            // =================================================

            data.append(
                "content",
                formData.content || ""
            );


            // =================================================
            // IMAGE
            // =================================================

            if (formData.image) {

                data.append(
                    "image",
                    formData.image
                );
            }


            // =================================================
            // PDF
            // =================================================

            if (formData.pdf) {

                data.append(
                    "pdf",
                    formData.pdf
                );
            }


            // =================================================
            // URL
            // =================================================

            let url =
                `${API_URL}/admin/free-study-material`;


            let method =
                "POST";


            // =================================================
            // UPDATE
            // =================================================

            if (editId) {

                url =
                    `${API_URL}/admin/free-study-material/${editId}`;

                data.append(
                    "_method",
                    "PUT"
                );
            }


            // =================================================
            // DEBUG
            // =================================================

            console.log(
                "SUBMIT DATA:",
                {
                    title:
                        formData.title,

                    subject:
                        formData.subject,

                    content:
                        formData.content,

                    image:
                        formData.image?.name ||
                        "No Image",

                    pdf:
                        formData.pdf?.name ||
                        "No PDF"
                }
            );


            // =================================================
            // API REQUEST
            // =================================================

            const response =
                await fetch(
                    url,
                    {
                        method,

                        headers: {
                            "Accept":
                                "application/json"
                        },

                        body: data,

                        credentials:
                            "include"
                    }
                );


            const result =
                await response.json();


            console.log(
                "SAVE RESPONSE:",
                result
            );


            // =================================================
            // SUCCESS
            // =================================================

            if (
                response.ok &&
                result.success
            ) {

                Swal.fire({
                    icon: "success",

                    title:
                        editId
                            ? "Study Material Updated"
                            : "Study Material Added",

                    text:
                        result.message,

                    timer: 1800,

                    showConfirmButton: false
                });


                resetForm();


                fetchMaterials(
                    currentPage
                );

            } else {

                let errorMessage =
                    result.message ||
                    "Unable to save study material.";


                if (result.errors) {

                    const validationMessages =
                        Object.values(
                            result.errors
                        )
                            .flat()
                            .join("\n");


                    if (validationMessages) {

                        errorMessage =
                            validationMessages;
                    }
                }


                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: errorMessage
                });
            }

        } catch (error) {

            console.error(
                "SAVE ERROR:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while saving study material."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // DELETE
    // =========================================================

    const handleDelete = async (id) => {

        const confirm =
            await Swal.fire({

                title:
                    "Delete Study Material?",

                text:
                    "Image, PDF and database record will be permanently deleted.",

                icon:
                    "warning",

                showCancelButton:
                    true,

                confirmButtonText:
                    "Yes, Delete",

                cancelButtonText:
                    "Cancel"
            });


        if (!confirm.isConfirmed) {

            return;
        }


        try {

            setSaving(true);


            const response =
                await fetch(
                    `${API_URL}/admin/free-study-material/${id}`,
                    {
                        method:
                            "DELETE",

                        headers: {
                            "Accept":
                                "application/json"
                        },

                        credentials:
                            "include"
                    }
                );


            const result =
                await response.json();


            console.log(
                "DELETE RESPONSE:",
                result
            );


            if (
                response.ok &&
                result.success
            ) {

                Swal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: result.message,
                    timer: 1500,
                    showConfirmButton: false
                });


                fetchMaterials(
                    currentPage
                );

            } else {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        result.message ||
                        "Unable to delete study material."
                });
            }

        } catch (error) {

            console.error(
                "DELETE ERROR:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while deleting study material."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // PAGE CHANGE
    // =========================================================

    const handlePageChange = (page) => {

        if (
            page < 1 ||
            page > lastPage
        ) {

            return;
        }


        fetchMaterials(page);
    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            className="container-fluid"
            style={{
                maxHeight: "85vh",
                overflowY: "auto"
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>

                    <h4 className="mb-1">
                        Free Study Material
                    </h4>

                    <p className="text-muted mb-0">
                        Manage free study materials
                    </p>

                </div>


                {!showForm && (

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAdd}
                    >

                        <FaPlus className="me-2" />

                        Add Study Material

                    </button>

                )}

            </div>


            {/* =================================================
                FORM
            ================================================= */}

            {showForm && (

                <div className="card shadow-sm mb-4">

                    <div className="card-header d-flex justify-content-between align-items-center">

                        <h5 className="mb-0">

                            {editId
                                ? "Edit Free Study Material"
                                : "Add Free Study Material"}

                        </h5>


                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetForm}
                            disabled={saving}
                        >

                            <FaTimes />

                        </button>

                    </div>


                    <div className="card-body">

                        <form onSubmit={handleSubmit}>

                            <div className="row">

                                {/* =================================================
                                    TITLE
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Title

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        name="title"
                                        className="form-control"
                                        value={
                                            formData.title
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={200}
                                        placeholder="Enter study material title"
                                    />

                                </div>


                                {/* =================================================
                                    SUBJECT
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Subject

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <select
                                        name="subject"
                                        className="form-select"
                                        value={
                                            formData.subject
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    >

                                        <option value="">
                                            -- Select Subject --
                                        </option>


                                        {subjects.map(
                                            (subject) => (

                                                <option
                                                    key={
                                                        subject.subject_name
                                                    }
                                                    value={
                                                        subject.subject_name
                                                    }
                                                >

                                                    {
                                                        subject.subject_name
                                                    }

                                                </option>

                                            )
                                        )}

                                    </select>


                                    {subjects.length === 0 && (

                                        <small className="text-danger">

                                            No active subjects found.

                                        </small>

                                    )}

                                </div>


                                {/* =================================================
                                    CONTENT
                                ================================================= */}

                                <div className="col-12 mb-4">

                                    <label className="form-label">

                                        Study Material Content

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.content
                                        }
                                        onChange={
                                            handleContentChange
                                        }
                                    />


                                    <small className="text-muted">

                                        Add explanation, notes,
                                        headings, images or other
                                        study material content.

                                    </small>

                                </div>


                                {/* =================================================
                                    IMAGE
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Study Material Image

                                        <span className="text-muted ms-1">
                                            (Optional)
                                        </span>

                                    </label>


                                    <input
                                        type="file"
                                        name="image"
                                        className="form-control"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={
                                            handleChange
                                        }
                                    />


                                    <small className="text-muted">

                                        JPG, PNG or WEBP.
                                        Maximum 5 MB.

                                    </small>


                                    {editId &&
                                        existingImage && (

                                            <div className="mt-3">

                                                <p className="mb-2">
                                                    Current Image:
                                                </p>


                                                <img
                                                    src={
                                                        `${IMAGE_STORAGE_URL}/${existingImage}`
                                                    }
                                                    alt="Current Study Material"
                                                    style={{
                                                        width:
                                                            "180px",

                                                        height:
                                                            "110px",

                                                        objectFit:
                                                            "cover",

                                                        borderRadius:
                                                            "6px",

                                                        border:
                                                            "1px solid #ddd"
                                                    }}
                                                />

                                            </div>

                                        )}

                                </div>


                                {/* =================================================
                                    PDF
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        PDF

                                        {!editId && (

                                            <span className="text-danger ms-1">
                                                *
                                            </span>

                                        )}

                                    </label>


                                    <input
                                        type="file"
                                        name="pdf"
                                        className="form-control"
                                        accept=".pdf,application/pdf"
                                        onChange={
                                            handleChange
                                        }
                                    />


                                    <small className="text-muted">

                                        PDF only.
                                        Maximum 20 MB.

                                        {editId &&
                                            " Leave empty to keep current PDF."}

                                    </small>


                                    {editId &&
                                        existingPdf && (

                                            <div className="mt-3">

                                                <a
                                                    href={
                                                        `${PDF_STORAGE_URL}/${existingPdf}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-danger"
                                                >

                                                    <FaFilePdf className="me-1" />

                                                    View Current PDF

                                                </a>

                                            </div>

                                        )}

                                </div>


                                {/* =================================================
                                    BUTTONS
                                ================================================= */}

                                <div className="col-12 mt-2">

                                    <button
                                        type="submit"
                                        className="btn btn-primary me-2"
                                        disabled={
                                            saving ||
                                            subjects.length === 0
                                        }
                                    >

                                        {saving
                                            ? "Saving..."
                                            : editId
                                                ? "Update Study Material"
                                                : "Add Study Material"
                                        }

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={resetForm}
                                        disabled={saving}
                                    >

                                        Cancel

                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            )}


            {/* =================================================
                TABLE
            ================================================= */}

            {!showForm && (

                <div className="card shadow-sm">

                    <div className="card-body p-0">

                        {loading ? (

                            <div className="text-center py-5">

                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                />

                                <p className="mt-2 mb-0">
                                    Loading study materials...
                                </p>

                            </div>

                        ) : materials.length === 0 ? (

                            <div className="text-center py-5">

                                <p className="text-muted mb-3">
                                    No study materials found.
                                </p>


                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAdd}
                                >

                                    <FaPlus className="me-2" />

                                    Add Study Material

                                </button>

                            </div>

                        ) : (

                            <div className="table-responsive">

                                <table className="table table-bordered table-hover align-middle mb-0">

                                    <thead className="table-light">

                                        <tr>

                                            <th
                                                style={{
                                                    width: "70px"
                                                }}
                                            >
                                                ID
                                            </th>

                                            <th
                                                style={{
                                                    width: "180px"
                                                }}
                                            >
                                                Image
                                            </th>

                                            <th>
                                                Title
                                            </th>

                                            <th>
                                                Subject
                                            </th>

                                            <th
                                                style={{
                                                    width: "150px"
                                                }}
                                            >
                                                PDF
                                            </th>

                                            <th
                                                style={{
                                                    width: "170px"
                                                }}
                                            >
                                                Date Added
                                            </th>

                                            <th
                                                style={{
                                                    width: "140px"
                                                }}
                                            >
                                                Action
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {materials.map(
                                            (material) => (

                                                <tr
                                                    key={
                                                        material.study_id
                                                    }
                                                >

                                                    {/* ID */}

                                                    <td>

                                                        {
                                                            material.study_id
                                                        }

                                                    </td>


                                                    {/* IMAGE */}

                                                    <td>

                                                        {material.image ? (

                                                            <img
                                                                src={
                                                                    `${IMAGE_STORAGE_URL}/${material.image}`
                                                                }
                                                                alt={
                                                                    material.title
                                                                }
                                                                style={{
                                                                    width:
                                                                        "150px",

                                                                    height:
                                                                        "90px",

                                                                    objectFit:
                                                                        "cover",

                                                                    borderRadius:
                                                                        "6px",

                                                                    border:
                                                                        "1px solid #ddd"
                                                                }}
                                                            />

                                                        ) : (

                                                            <span className="text-muted">

                                                                No Image

                                                            </span>

                                                        )}

                                                    </td>


                                                    {/* TITLE */}

                                                    <td>

                                                        <strong>

                                                            {
                                                                material.title
                                                            }

                                                        </strong>

                                                    </td>


                                                    {/* SUBJECT */}

                                                    <td>

                                                        {
                                                            material.subject
                                                        }

                                                    </td>


                                                    {/* PDF */}

                                                    <td>

                                                        {material.download ? (

                                                            <a
                                                                href={
                                                                    `${PDF_STORAGE_URL}/${material.download}`
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-danger"
                                                            >

                                                                <FaDownload className="me-1" />

                                                                Download

                                                            </a>

                                                        ) : (

                                                            <span className="text-muted">

                                                                No PDF

                                                            </span>

                                                        )}

                                                    </td>


                                                    {/* DATE */}

                                                    <td>

                                                        {
                                                            material.date_added
                                                        }

                                                    </td>


                                                    {/* ACTION */}

                                                    <td>

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-warning me-2"
                                                            onClick={() =>
                                                                handleEdit(
                                                                    material.study_id
                                                                )
                                                            }
                                                            disabled={
                                                                saving
                                                            }
                                                            title="Edit"
                                                        >

                                                            <FaEdit />

                                                        </button>


                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    material.study_id
                                                                )
                                                            }
                                                            disabled={
                                                                saving
                                                            }
                                                            title="Delete"
                                                        >

                                                            <FaTrash />

                                                        </button>

                                                    </td>

                                                </tr>

                                            )
                                        )}

                                    </tbody>

                                </table>

                            </div>

                        )}

                    </div>


                    {/* =================================================
                        PAGINATION
                    ================================================= */}

                    {!loading &&
                        materials.length > 0 &&
                        lastPage > 1 && (

                            <div className="card-footer">

                                <nav>

                                    <ul className="pagination mb-0 justify-content-center">

                                        {/* PREVIOUS */}

                                        <li
                                            className={
                                                `page-item ${
                                                    currentPage === 1
                                                        ? "disabled"
                                                        : ""
                                                }`
                                            }
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() =>
                                                    handlePageChange(
                                                        currentPage - 1
                                                    )
                                                }
                                                disabled={
                                                    currentPage === 1
                                                }
                                            >

                                                Previous

                                            </button>

                                        </li>


                                        {/* PAGE NUMBERS */}

                                        {Array.from(
                                            {
                                                length:
                                                    lastPage
                                            },
                                            (_, index) =>
                                                index + 1
                                        ).map(
                                            (page) => (

                                                <li
                                                    key={page}
                                                    className={
                                                        `page-item ${
                                                            currentPage === page
                                                                ? "active"
                                                                : ""
                                                        }`
                                                    }
                                                >

                                                    <button
                                                        type="button"
                                                        className="page-link"
                                                        onClick={() =>
                                                            handlePageChange(
                                                                page
                                                            )
                                                        }
                                                    >

                                                        {page}

                                                    </button>

                                                </li>

                                            )
                                        )}


                                        {/* NEXT */}

                                        <li
                                            className={
                                                `page-item ${
                                                    currentPage === lastPage
                                                        ? "disabled"
                                                        : ""
                                                }`
                                            }
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() =>
                                                    handlePageChange(
                                                        currentPage + 1
                                                    )
                                                }
                                                disabled={
                                                    currentPage === lastPage
                                                }
                                            >

                                                Next

                                            </button>

                                        </li>

                                    </ul>

                                </nav>

                            </div>

                        )}

                </div>

            )}

        </div>
    );
};


export default FreeStudyMaterial;